// apps/web/src/components/shell/AppShell.jsx
// Single shell for ALL routes (hub + workflows): optional SideNav + AppTopbar + main + footer.

import React, { useCallback, useState } from "react";
import { AppTopbar } from "./AppTopbar.jsx";
import { AppFooter } from "./AppFooter.jsx";
import {
  PlatformSideNav,
  PlatformSideNavMobileToggle,
} from "../platform/PlatformSideNav.jsx";

export function AppShell({
  t,
  authenticated = false,
  authUserLabel = "",
  authUserEmail = "",
  authUserAvatar = "",
  authUserInitials = "",
  /** @type {{ accessReady?: boolean, isSuperAdmin?: boolean, allowedAppKeys?: Set<string>|null }} */
  launcherAccess = null,
  onSignOut,
  onSignIn,
  enablePlatformAppSwitcher = false,
  canAccessAccessRequestsAdmin = false,
  canAccessVereinsabo = false,
  subscriptionFeatures = null,
  bypassSubscriptionFeatures = false,
  shellAppTitleKey = "",
  shellAppSubtitleKey = "",
  /** Katalog-appKey der aktuell eingebetteten App (z. B. trainerstunden) – für Shell-CSS/Topbar-Varianten. */
  shellScopeAppKey = "",
  /** Login/Auth-Callback: volle Viewport-Fläche ohne Plattform-Topbar (Footer bleibt für Impressum/Datenschutz). */
  hidePlatformChrome = false,
  /** Ausstehende Zugriffsanfrage ohne App-Rechte: Launcher/Einstellungen sperren. */
  shellAccessRestricted = false,
  /** Persönliche Startseite (Route-Name). */
  homeRoute = "hub",
  onGoHome,
  coachDeskSelfProfileHref = "",
  /** Aktuell auf der globalen Startseite – Logo öffnet keinen Launcher. */
  homeMode = false,
  /** Vereine des Nutzers für Avatar-Menü-Sektion „Meine Vereine". */
  userClubs = null,
  children,
  footer = true,
}) {
  const scope = String(shellScopeAppKey || "").trim();
  const showFooter = footer;
  const showSideNav = Boolean(authenticated) && !hidePlatformChrome;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const openMobileNav = useCallback(() => setMobileNavOpen(true), []);
  const onMobileOpenChange = useCallback(
    (open) => setMobileNavOpen(!!open),
    [],
  );

  return (
    <div
      className={`app-shell${hidePlatformChrome ? " app-shell--authFullscreen" : ""}${showSideNav ? " app-shell--withSideNav" : ""}${showSideNav ? " app-shell--noTopbar" : ""}`}
      data-shell="app"
      {...(scope ? { "data-shell-app": scope } : {})}
      {...(authenticated ? { "data-shell-authenticated": "true" } : {})}
    >
      {showSideNav ? (
        <PlatformSideNav
          t={t}
          launcherAccess={launcherAccess}
          shellAccessRestricted={shellAccessRestricted}
          mobileOpen={mobileNavOpen}
          onMobileOpenChange={onMobileOpenChange}
          onGoHome={onGoHome}
          authUserAvatar={authUserAvatar}
          authUserInitials={authUserInitials}
          authUserLabel={authUserLabel}
          authUserEmail={authUserEmail}
          onSignOut={onSignOut}
          canAccessAccessRequestsAdmin={canAccessAccessRequestsAdmin}
          canAccessVereinsabo={canAccessVereinsabo}
          subscriptionFeatures={subscriptionFeatures}
          bypassSubscriptionFeatures={bypassSubscriptionFeatures}
          coachDeskSelfProfileHref={coachDeskSelfProfileHref}
          homeMode={homeMode}
          userClubs={userClubs}
        />
      ) : null}
      <div className={showSideNav ? "app-shell__column" : undefined}>
        {showSideNav ? (
          <div className="app-shell__mobileNavToggle">
            <PlatformSideNavMobileToggle
              t={t}
              open={mobileNavOpen}
              onClick={() => {
                if (shellAccessRestricted) return;
                openMobileNav();
              }}
            />
          </div>
        ) : hidePlatformChrome ? null : (
          <AppTopbar
            t={t}
            shellAppTitleKey={shellAppTitleKey}
            shellAppSubtitleKey={shellAppSubtitleKey}
            authenticated={authenticated}
            authUserLabel={authUserLabel}
            authUserEmail={authUserEmail}
            authUserAvatar={authUserAvatar}
            authUserInitials={authUserInitials}
            launcherAccess={launcherAccess}
            onSignOut={onSignOut}
            onSignIn={onSignIn}
            enablePlatformAppSwitcher={enablePlatformAppSwitcher}
            canAccessAccessRequestsAdmin={canAccessAccessRequestsAdmin}
            canAccessVereinsabo={canAccessVereinsabo}
            coachDeskDesktopUserChrome={scope === "trainerstunden"}
            shellAccessRestricted={shellAccessRestricted}
            homeRoute={homeRoute}
            onGoHome={onGoHome}
            coachDeskSelfProfileHref={coachDeskSelfProfileHref}
            homeMode={homeMode}
            userClubs={userClubs}
            sideNavEnabled={false}
            onOpenSideNav={openMobileNav}
            sideNavMobileOpen={mobileNavOpen}
          />
        )}
        <main className="app-shell__main" id="page-root">
          <div className="app-shell__content">{children}</div>
        </main>
        {showFooter ? <AppFooter t={t} authenticated={authenticated} /> : null}
      </div>
    </div>
  );
}
